import { Box, IconButton, IconButtonProps, Popover } from '@mui/material';
import { useState } from 'react';
import DotsIcon from '../../../icons/svg/dots_menu.svg?react';
import { ThreePointMenuList } from './threePointMenuList';

type MenuItemProps<T> = {
  label: string;
  isWarning?: boolean;
  onClick: (item: T) => void;
};

type MenuItemWithIconProps<T> = MenuItemProps<T> & {
  icon: JSX.Element;
};

type Props<T = undefined> = {
  config: Array<MenuItemProps<T>> | Array<MenuItemWithIconProps<T>>;
  item: T;
  sx?: IconButtonProps['sx'];
  disableRipple?: boolean;
};

export type ThreePointMenuProps<T = undefined> = Props<T>;

export function ThreePointMenu<T>({ config, item, sx }: Props<T>): JSX.Element {
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);
  const closeMenu = () => setAnchorElement(null);

  return (
    <Box onClick={(ev) => ev.preventDefault()}>
      <IconButton
        sx={{
          padding: 0,
          borderRadius: '8px',
          color: 'primary.main',
          '&:hover, &:active': {
            color: 'grey.900'
          },
          ...sx
        }}
        disableRipple
        onClick={({ currentTarget }) => setAnchorElement(currentTarget)}
      >
        <DotsIcon />
      </IconButton>
      <Popover
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        BackdropProps={{
          invisible: true,
          style: { backgroundColor: 'transparent', opacity: '0' }
        }}
        PaperProps={{
          className: 'shadow-500'
        }}
      >
        <ThreePointMenuList<T> config={config} closeMenu={closeMenu} item={item} />
      </Popover>
    </Box>
  );
}

export function isMenuItemWithIcon<T>(
  menuItem: MenuItemProps<T> | MenuItemWithIconProps<T>
): menuItem is MenuItemWithIconProps<T> {
  return !!(menuItem as MenuItemWithIconProps<T>).icon;
}
